const ISO_DATE_PREFIX = /^(\d{4})-(\d{2})-(\d{2})/;
const THAI_SLASH_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{2,4})$/;
const THAI_MONTH_ABBR = [
  "ม.ค.", "ก.พ.", "มี.ค.", "เม.ย.", "พ.ค.", "มิ.ย.",
  "ก.ค.", "ส.ค.", "ก.ย.", "ต.ค.", "พ.ย.", "ธ.ค."
];

function isLocaleEn(locale?: string): boolean {
  return locale === "en";
}

function formatDayMonthYy(day: number, month: number, beYearFull: number, locale?: string): string {
  if (isLocaleEn(locale)) {
    const adYearFull = beYearFull >= 2500 ? beYearFull - 543 : beYearFull;
    return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${String(adYearFull).slice(-2)}`;
  }
  const yy = String(beYearFull).slice(-2);
  return `${String(day).padStart(2, "0")}/${String(month).padStart(2, "0")}/${yy}`;
}

export function normalizeToISODate(value: string | null | undefined): string {
  if (value == null || value === "") return "";

  const trimmed = value.trim();

  const isoMatch = trimmed.match(ISO_DATE_PREFIX);
  if (isoMatch) {
    return `${isoMatch[1]}-${isoMatch[2]}-${isoMatch[3]}`;
  }

  const thaiMatch = trimmed.match(THAI_SLASH_DATE);
  if (thaiMatch) {
    const day = parseInt(thaiMatch[1]!, 10);
    const month = parseInt(thaiMatch[2]!, 10);
    let year = parseInt(thaiMatch[3]!, 10);

    if (year < 100) {
      year = 2500 + year;
    }

    if (year > 2500) {
      year -= 543;
    }

    if (month >= 1 && month <= 12 && day >= 1 && day <= 31) {
      return `${year}-${String(month).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
    }
  }

  const d = new Date(trimmed);
  if (!Number.isNaN(d.getTime())) {
    const y = d.getFullYear();
    const m = d.getMonth() + 1;
    const dy = d.getDate();
    return `${y}-${String(m).padStart(2, "0")}-${String(dy).padStart(2, "0")}`;
  }

  return "";
}

export function formatThaiDateDdMmYy(input: string | Date | null | undefined, locale?: string): string {
  if (input == null || input === "") return "";

  if (input instanceof Date) {
    if (Number.isNaN(input.getTime())) return "";
    const d = input.getDate();
    const m = input.getMonth() + 1;
    const gy = input.getFullYear();
    const be = gy < 2500 ? gy + 543 : gy;
    return formatDayMonthYy(d, m, be, locale);
  }

  if (typeof input === "string") {
    const m = input.match(ISO_DATE_PREFIX);
    if (m) {
      const y = parseInt(m[1], 10);
      const mo = parseInt(m[2], 10);
      const d = parseInt(m[3], 10);
      if (y >= 2500) {
        return formatDayMonthYy(d, mo, y, locale);
      }
      const be = y + 543;
      return formatDayMonthYy(d, mo, be, locale);
    }
    const dt = new Date(input);
    if (!Number.isNaN(dt.getTime())) {
      return formatThaiDateDdMmYy(dt, locale);
    }
  }

  return "";
}

export function formatThaiDateDdMmYyLoose(input: string | null | undefined, locale?: string): string {
  if (input == null || input === "") return "";
  const t = input.trim();
  if (isLocaleEn(locale)) {
    if (/^\d{2}\/\d{2}\/\d{4}$/.test(t)) return t;
    const m2 = t.match(/^(\d{2})\/(\d{2})\/(\d{2})$/);
    if (m2) {
      const yy = parseInt(m2[3]!, 10);
      const adGuess = yy < 50 ? 2000 + yy : 1900 + yy;
      return `${m2[1]}/${m2[2]}/${adGuess}`;
    }
    return formatThaiDateDdMmYy(input, locale);
  }
  if (/^\d{2}\/\d{2}\/\d{2}$/.test(t)) return t;
  const m4 = t.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (m4) {
    return `${m4[1]}/${m4[2]}/${m4[3]!.slice(-2)}`;
  }
  return formatThaiDateDdMmYy(input, locale);
}

export function formatThaiDateTimeDdMmYy(iso: string | null | undefined, locale?: string): string {
  if (iso == null || iso === "") return "";
  const dt = new Date(iso);
  if (Number.isNaN(dt.getTime())) return "";
  const datePart = formatThaiDateDdMmYy(dt, locale);
  const h = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${datePart} ${h}:${min}`;
}

export function formatThaiMonthYearDdMmYy(yyyyMm: string, locale?: string): string {
  if (!yyyyMm) return "";
  const [yStr, mStr] = yyyyMm.split("-");
  const y = parseInt(yStr, 10);
  const m = parseInt(mStr, 10);
  if (Number.isNaN(y) || Number.isNaN(m) || m < 1 || m > 12) return "";
  if (isLocaleEn(locale)) {
    const d = new Date(y, m - 1, 1);
    const monthAbbr = d.toLocaleDateString("en-GB", { month: "short" });
    return `${monthAbbr.charAt(0).toUpperCase()}${monthAbbr.slice(1)} ${y}`;
  }
  const be = y < 2500 ? y + 543 : y;
  const beShort = be % 100;
  return `${THAI_MONTH_ABBR[m - 1]} ${String(beShort).padStart(2, "0")}`;
}

export function formatThaiDateRangeDdMmYy(
  start: string,
  end: string,
  locale?: string
): { dateRange: string; days: string } {
  const startFormatted = formatThaiDateDdMmYy(start, locale);
  const endFormatted = formatThaiDateDdMmYy(end, locale);
  const startDate = new Date(start);
  const endDate = new Date(end);
  const dayMs = 1000 * 60 * 60 * 24;
  const days = Math.ceil((endDate.getTime() - startDate.getTime()) / dayMs) + 1;
  return { dateRange: `${startFormatted} - ${endFormatted}`, days: String(days) };
}
